import Vue from 'vue';
import Vuex from 'vuex';

Vue.use(Vuex);

export default new Vuex.Store({
    state: {
        players: [],
        gameState: {
            time: null,
            globalStatus: null,
            tasks: {
                main: {
                    fileUrl: null,
                    author: null,
                    visibleFor: [],
                    visibility: false,
                    is_filled: null,
                    text: null,
                    video: null,
                    updated_at: null,
                    is_video_started: false,
                },
                additional: {
                    fileUrl: null,
                    author: null,
                    visibleFor: [],
                    visibility: false,
                    is_filled: null,
                    text: null,
                    video: null,
                    updated_at: null,
                    is_video_started: false,
                },
                state: {
                    type: null,
                    game_started: false,
                    activity_id: null,
                    activity: null,
                    content_pack_id: null,
                    set_id: null,
                    sets_count: null,
                    show_end_button: false,
                    current_set_id: null,
                    show_play_again_button: false,
                    zone_type: null,
                    showRoundName: true,
                }
            },
            timer: {
                endTime: null,
                status: false,
                visible: true,
            },
            playersData: {},
            automation: null,
            zones: {
                task_buttons: {
                    enabled: false,
                    background_color: null
                },
                chat: {
                    buttons: {
                        enabled: false,
                        five_traps: {},
                        mafia: null,
                    },
                    disabledFor: [],
                    disabledPlaceholder: null,
                }
            }
        },
        gameField: null,
        activitiesList: [],
        playerId: null,
        accessRights: {},
        devicesStatus: {
            cameraIsOn: false,
            microphoneIsOn: false
        },
        typesCatalogFilters: null,
        gameplayCatalogFilters: null,
        painterEditMode: false,
        automationsList: null,
        allowConference: null,
        isMini: false,
        apiUrls: {
            subscribeUrl: null
        }
    },
    getters: {
        /**
         * Получить список всех игроков
         * @param state
         * @returns {[]}
         */
        getPlayers: state => {
            return state.players;
        },

        /**
         * Инициализация конференции разрешена
         * @param state
         * @returns {boolean}
         */
        getConferenceIsAllowed: state => {
            return state.allowConference;
        },
        getGameState: state => {
            return state.gameState;
        },
        getGameField: state => {
            return state.gameField;
        },
        getAutomationsList: state => {
            return state.automationsList;
        },
        /**
         * Получить список всех игр
         * @param state
         * @returns {[]}
         */
        getActivitiesList: state => {
            return state.activitiesList;
        },
        /**
         * Получить игру по id
         * @param state
         * @returns {[]}
         */
        getActivityById: (state, getters) => (id) => {
            const filteredActivities = getters.getActivitiesList.filter(activity => activity.id === id);

            if (typeof filteredActivities[0] === 'undefined') {
                return null;
            }

            return filteredActivities[0];
        },
        /**
         * Получить данные игрока: цвет, видео, статусы, id
         * @param state
         * @param getters
         * @returns {(function(*): (null|T))|*}
         */
        getPlayerDataById: (state, getters) => (id) => {
            const filteredPlayers = getters.getPlayers.filter(player => player.id === id);

            if (typeof filteredPlayers[0] === 'undefined') {
                return null;
            }

            return filteredPlayers[0];
        },
        /**
         * Получить игровые данные игрока: очки, иконка, имя, id
         * @param state
         * @param getters
         * @returns {(function(*): (null|T))|*}
         */
        getPlayerGameDataById: (state, getters) => (id) => {
            const players = getters.getGameState.playersData;
            let player = null;

            if (players.hasOwnProperty(id)) {
                player = players[id];
            }

            return player;
        },
        /**
         * Получить id текущего игрока
         * @param state
         * @returns {null}
         */
        getPlayerId: state => {
            return state.playerId;
        },
        getAccessRights: state => {
            return state.accessRights;
        },
        /**
         * Получить текущую игру
         * @param state
         * @returns {[]}
         */
        getCurrentActivity: state => {
            return state.gameState.tasks.state.activity;
        },
        getCameraStatus: state => {
            return state.devicesStatus.cameraIsOn;
        },
        getMicrophoneStatus: state => {
            return state.devicesStatus.microphoneIsOn;
        },
        getTypesCatalogFilters: state => {
            return state.typesCatalogFilters
        },
        getPainterEditMode: state => {
            return state.painterEditMode;
        },
        /**
         * Получить фильтры для каталога
         * @param state
         * @returns {[]}
         */
        getGameplayCatalogFilters: state => {
            return state.gameplayCatalogFilters
        },
        /**
         * Получить статус соединения текущего игрока
         * @param state
         * @returns {[]}
         */
        getPlayerConnectionStatus: state => {
            let player = null;
            if (!state.players.length) {
                return 'guest';
            }
            const filteredPlayers = state.players.filter(item => {
                return item.id === state.playerId;
            });

            if (typeof filteredPlayers[0] !== 'undefined') {
                player = filteredPlayers[0];
            }

            if (!player) {
                return 'guest';
            }

            return player.connectionStatus;
        },
        /**
         * Получить карту текущей автоматизации
         * @param state
         * @returns {[]}
         */
        getPlayersAutomationMap: state => {
            const automation = state.gameState.automation;

            if (!automation) {
                return null;
            }

            return automation.playersMap;
        },
        /**
         * Получить роль игрока в текущей автоматизации по ID
         * @param state
         * @returns {[]}
         */
        getPlayerAutomationRole: (state, getters) => (id) => {
            const playersMap = getters.getPlayersAutomationMap;
            if (!playersMap) {
                return null;
            }

            if (typeof playersMap[id] === 'undefined') {
                return null;
            }

            return playersMap[id].role;
        },
        /**
         * Получить статус игрока в текущей автоматизации по ID
         * @param state
         * @returns {[]}
         */
        getPlayerAutomationStatus: (state, getters) => (id) => {
            const playersMap = getters.getPlayersAutomationMap;
            if (!playersMap) {
                return null;
            }

            if (typeof playersMap[id] === 'undefined') {
                return null;
            }

            return playersMap[id].status;
        },
        /**
         * Получить тип текущего лейаута
         * @param state
         * @returns {[]}
         */
        getCurrentLayoutType: (state, getters) => {
            const activity = getters.getCurrentActivity;
            if (activity) {
                const type = activity.settings.layoutType;

                switch(type) {
                    case 1:
                        return 'default-layout';
                    case 2:
                        return 'table-layout';
                    case 3:
                        return 'task-layout';
                    case 4:
                        return 'seat-layout';
                    case 5:
                        return 'seat-on-table';
                    default:
                        return 'default-layout';
                }
            }

            return 'default-layout';
        },
        /**
         * Получить признак, является ли Фестой-мини
         * @param state
         * @returns {boolean}
         */
        getIsMini: state => {
            return state.isMini;
        },
        /**
         * Получить признак, является ли Фестой-мини
         * @param state
         * @returns {boolean}
         */
        getSubscribeUrl: state => {
            return state.apiUrls.subscribeUrl;
        }
    },
    mutations: {
        setPlayers: (state, payload) => {
            state.players = payload;
        },
        setConferenceIsAllowed: (state, payload) => {
            state.allowConference = payload;
        },
        setGameState: (state, payload) => {
            state.gameState = payload;
        },
        setGameField: (state, payload) => {
            state.gameField = payload;
        },
        setActivitiesList: (state, payload) => {
            state.activitiesList = payload
        },
        setPlayerId: (state, payload) => {
            state.playerId = payload
        },
        setAccessRights: (state, payload) => {
            state.accessRights = payload;
        },
        setCameraStatus: (state, payload) => {
            state.devicesStatus.cameraIsOn = payload;
        },
        setMicrophoneStatus: (state, payload) => {
            state.devicesStatus.microphoneIsOn = payload;
        },
        setPainterEditMode: (state, payload) => {
            state.painterEditMode = payload;
        },
        setTypesCatalogFilters: (state, payload) => {
            state.typesCatalogFilters = payload;
        },
        setGameplayCatalogFilters: (state, payload) => {
            state.gameplayCatalogFilters = payload;
        },
        setAutomationsList: (state, payload) => {
            state.automationsList = payload;
        },
        setActiveTypeCatalogFilter: (state, id) => {
            const filters = state.typesCatalogFilters;
            if (!filters) {
                state.typesCatalogFilters = null;
            }
            filters.forEach(filter => {
               filter.isActive = false;
               if (filter.id === id) {
                   filter.isActive = true;
               }
            });
        },
        setActiveGameplayCatalogFilter: (state, id) => {
            const filters = state.gameplayCatalogFilters;
            if (!filters) {
                state.gameplayCatalogFilters = null;
            }
            filters.forEach(filter => {
               filter.isActive = false;
               if (filter.id === id) {
                   filter.isActive = true;
               }
            });
        },
        setIsMini: (state, value) => {
            state.isMini = value;
        },
        setSubscribeUrl: (state, value) => {
            state.apiUrls.subscribeUrl = value;
        }
    },
    actions: {
        setGameField: (context, field) => {
            return new Promise((resolve, reject) => {
                context.commit('setGameField', field);
                resolve();
            });
        },
        setCameraStatus: (context, status) => {
            context.commit('setCameraStatus', status);
        },
        setConferenceIsAllowed: (context, status) => {
            context.commit('setConferenceIsAllowed', status);
        },
        setMicrophoneStatus: (context, status) => {
            context.commit('setMicrophoneStatus', status);
        },
        setTypesCatalogFilters: (context, status) => {
            context.commit('setTypesCatalogFilters', status);
        },
        setGameplayCatalogFilters: (context, status) => {
            context.commit('setGameplayCatalogFilters', status);
        },
        setActiveTypeCatalogFilter: (context, id) => {
            context.commit('setActiveTypeCatalogFilter', id);
        },
        setActiveGameplayCatalogFilter: (context, id) => {
            context.commit('setActiveGameplayCatalogFilter', id);
        },
        setPainterEditMode: (context, payload) => {
            context.commit('setPainterEditMode', payload);
        },
        setAutomationsList: (context, payload) => {
            context.commit('setAutomationsList', payload);
        },
        setIsMini: (context, payload) => {
            context.commit('setIsMini', payload);
        },
        setSubscribeUrl: (context, payload) => {
            context.commit('setSubscribeUrl', payload);
        }
    }
});
